import { toast } from "sonner";
import { composeShort, composeClear } from "../ClarityCheck";
import { copyText } from "../../lib/copy";

const NO_MORE = "I have already explained this. I am not going to keep restating it.";

export default function MessageBuilderStep({ state, update, onSkip }) {
  const short = composeShort(state);
  const clear = composeClear(state);

  const copy = async (text, label) => {
    const ok = await copyText(text);
    if (ok) {
      toast(`${label} copied.`);
    } else {
      toast("Couldn't copy automatically — long-press the preview text to copy.");
    }
  };

  return (
    <div className="space-y-10">
      <p className="text-[17px] leading-[1.6] text-[color:var(--ks-ink-soft)] -mt-2">
        Three scripts. Fill the blanks or leave them. Copy what lands. Skip if you're not messaging anyone.
      </p>

      {/* Short version */}
      <section className="ks-card p-5 sm:p-6">
        <p className="ks-kicker mb-2">Script 01</p>
        <h3 className="ks-h3 mb-5">The short version</h3>
        <p className="text-[17px] leading-[1.7]">
          I am not available for{" "}
          <input
            className="ks-input inline-block !w-auto min-w-[140px]"
            value={state.s6_short_a}
            onChange={(e) => update({ s6_short_a: e.target.value })}
            placeholder="what you won't do"
            data-testid="mb-short-a"
          />
          . I am going to{" "}
          <input
            className="ks-input inline-block !w-auto min-w-[140px]"
            value={state.s6_short_b}
            onChange={(e) => update({ s6_short_b: e.target.value })}
            placeholder="what you will do"
            data-testid="mb-short-b"
          />
          .
        </p>
        <div className="mt-5 p-4 border ks-hair rounded-xl bg-[#0f0f11]">
          <p className="ks-kicker mb-2">Preview</p>
          <p className="text-[16px] leading-[1.55]" data-testid="mb-short-preview">{short}</p>
        </div>
        <button
          className="ks-btn ks-btn-ghost mt-4"
          onClick={() => copy(short, "Short version")}
          data-testid="mb-short-copy"
        >
          Copy this one
        </button>
      </section>

      {/* Clear version */}
      <section className="ks-card p-5 sm:p-6">
        <p className="ks-kicker mb-2">Script 02</p>
        <h3 className="ks-h3 mb-5">The clear version</h3>
        <p className="text-[17px] leading-[1.7]">
          When{" "}
          <input
            className="ks-input inline-block !w-auto min-w-[140px]"
            value={state.s6_clear_a}
            onChange={(e) => update({ s6_clear_a: e.target.value })}
            placeholder="what happened"
            data-testid="mb-clear-a"
          />{" "}
          happened, I felt{" "}
          <input
            className="ks-input inline-block !w-auto min-w-[120px]"
            value={state.s6_clear_b}
            onChange={(e) => update({ s6_clear_b: e.target.value })}
            placeholder="how you felt"
            data-testid="mb-clear-b"
          />
          . What I need moving forward is{" "}
          <input
            className="ks-input inline-block !w-auto min-w-[160px]"
            value={state.s6_clear_c}
            onChange={(e) => update({ s6_clear_c: e.target.value })}
            placeholder="what you need"
            data-testid="mb-clear-c"
          />
          .
        </p>
        <div className="mt-5 p-4 border ks-hair rounded-xl bg-[#0f0f11]">
          <p className="ks-kicker mb-2">Preview</p>
          <p className="text-[16px] leading-[1.55]" data-testid="mb-clear-preview">{clear}</p>
        </div>
        <button
          className="ks-btn ks-btn-ghost mt-4"
          onClick={() => copy(clear, "Clear version")}
          data-testid="mb-clear-copy"
        >
          Copy this one
        </button>
      </section>

      {/* No more paragraphs */}
      <section className="ks-card p-5 sm:p-6">
        <p className="ks-kicker mb-2">Script 03</p>
        <h3 className="ks-h3 mb-5">The no-more-paragraphs version</h3>
        <p className="text-[17px] leading-[1.7]" data-testid="mb-nomore-text">{NO_MORE}</p>
        <button
          className="ks-btn ks-btn-ghost mt-4"
          onClick={() => copy(NO_MORE, "No-more-paragraphs version")}
          data-testid="mb-nomore-copy"
        >
          Copy this one
        </button>
      </section>

      <div className="pt-2">
        <button
          className="font-elite uppercase tracking-[0.08em] text-[13px] text-[color:var(--ks-ink-soft)] hover:text-[color:var(--ks-fuchsia)]"
          onClick={onSkip}
          data-testid="mb-skip-link"
        >
          Not messaging anyone — skip this step
        </button>
      </div>
    </div>
  );
}
